import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { Eye, EyeOff } from "lucide-react";
import { useAppContext } from "@/providers";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/layout/logo";
import { t } from "@/lib/i18n/messages";
import { cn } from "@/lib/utils";
import { apiClient } from "@/lib/api/client";

export default function SetPasswordPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { locale } = useAppContext();
  const isRTL = locale === "ar";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<{ password?: string; confirm?: string; global?: string }>({});

  const validate = () => {
    const e: typeof errors = {};
    if (password.length < 8) e.password = t(locale, "passwordMinLength");
    if (password !== confirmPassword) e.confirm = t(locale, "passwordMismatch");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setErrors({});

    const result = await apiClient.post(`/api_partner/enablers/request/${code}/set-password`, {
      password,
      password_confirmation: confirmPassword,
    });

    setSubmitting(false);

    if (result.success) {
      setDone(true);
      setTimeout(() => navigate("/login"), 3000);
    } else {
      setErrors({ global: result.message ?? (isRTL ? "حدث خطأ، يرجى التحقق من الرمز." : "An error occurred. Please check the code.") });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="border-b border-border">
        <div className="px-6 md:px-10 h-16 flex items-center">
          <Logo size="md" />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[420px] space-y-8">
          <div className="space-y-3">
            <span className="inline-flex items-center gap-2 text-[11px] font-sans tracking-[0.2em] text-foreground-secondary border border-border rounded-full px-3 py-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />
              {isRTL ? "تفعيل الحساب" : "ACCOUNT SETUP"}
            </span>
            <h1 className="font-display font-bold text-3xl md:text-4xl text-foreground tracking-tight leading-[1.1]">
              {isRTL ? "أنشئ كلمة المرور" : "Create your password"}
            </h1>
            <p className="text-foreground-secondary text-[15px] leading-relaxed">
              {isRTL
                ? "تمت الموافقة على طلبك. أنشئ كلمة مرور لتفعيل حسابك والبدء."
                : "Your request has been approved. Set a password to activate your account."}
            </p>
          </div>

          {done ? (
            <div className="rounded-2xl border border-green-500/30 bg-green-500/5 px-5 py-6 text-center space-y-2">
              <p className="font-bold text-green-600 dark:text-green-400 text-[15px]">
                {isRTL ? "تم تفعيل حسابك بنجاح!" : "Account activated successfully!"}
              </p>
              <p className="text-foreground-secondary text-sm">
                {isRTL ? "سيتم تحويلك لصفحة تسجيل الدخول…" : "Redirecting you to login…"}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                required
                label={t(locale, "newPassword")}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                error={errors.password}
                trailingIcon={
                  <button type="button" onClick={() => setShowPassword((v) => !v)}
                    className="hover:text-foreground transition-colors" tabIndex={-1}>
                    {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.8} /> : <Eye className="w-4 h-4" strokeWidth={1.8} />}
                  </button>
                }
              />

              <Input
                id="confirm-password"
                name="password_confirmation"
                type={showConfirm ? "text" : "password"}
                autoComplete="new-password"
                required
                label={t(locale, "confirmPassword")}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={submitting}
                error={errors.confirm}
                trailingIcon={
                  <button type="button" onClick={() => setShowConfirm((v) => !v)}
                    className="hover:text-foreground transition-colors" tabIndex={-1}>
                    {showConfirm ? <EyeOff className="w-4 h-4" strokeWidth={1.8} /> : <Eye className="w-4 h-4" strokeWidth={1.8} />}
                  </button>
                }
              />

              {errors.global && (
                <div className="rounded-2xl border border-red-500/30 bg-red-500/5 px-4 py-3">
                  <p className="font-sans text-[11px] tracking-widest text-red-500 uppercase">{errors.global}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className={cn(
                  "w-full h-12 inline-flex items-center justify-center gap-2",
                  "bg-primary hover:bg-primary-dark text-white rounded-pill",
                  "font-bold text-[15px] shadow-sm transition-all active:scale-[0.98]",
                  "disabled:opacity-60 disabled:cursor-not-allowed",
                )}
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                    <span>{isRTL ? "جارٍ الحفظ…" : "Saving…"}</span>
                  </span>
                ) : (
                  isRTL ? "تفعيل الحساب" : "Activate Account"
                )}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
